import {
  ChangeDetectionStrategy,
  computed,
  Component,
  input,
  output,
  model,
  effect,
  inject,
  signal,
  untracked,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateRange, DateRangeFieldComponent } from 'nebula-ui-kit';
import { catchError, of, tap } from 'rxjs';
import {
  AlertStore,
  TranslationService,
  DateRangePickerServices,
  Module,
} from '@nebula-shared/data-access';

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [FormsModule, DateRangeFieldComponent],
  templateUrl: './date-range-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangePickerComponent extends AlertStore implements OnInit {
  protected service = inject(DateRangePickerServices);
  protected translation = inject(TranslationService);

  protected t(key: string): string {
    return this.translation.translate(key);
  }

  start = input<Date | null>(null);
  end = input<Date | null>(null);
  minDate = input<Date | null>(null);
  maxDate = input<Date | null>(null);
  module = input<Module>('accounting');
  periodName = input<boolean>(true);
  duration = input<boolean>(true);

  /**
   * Si el modelo ya trae un rango válido al crearse el selector, se respeta en lugar de
   * reponer el mes en curso. Sirve a las pantallas que conservan el filtro al volver a ellas.
   */
  conservarRango = input<boolean>(false);

  startLabel = input<string>('');
  endLabel = input<string>('');

  /**
   * Rotulos que se pintan: los que manda la pantalla o, si no manda ninguno, los propios del
   * selector en el idioma vigente. Como valor por defecto del input se traducian una sola vez y
   * se quedaban en el idioma con que se abrio la pantalla (Mantis #327).
   */
  protected readonly rotuloInicio = computed(
    () => this.startLabel() || this.t('ACCOUNTING.ANNULLMENT.PLACEHOLDER.DATE_START'),
  );
  protected readonly rotuloFin = computed(
    () => this.endLabel() || this.t('ACCOUNTING.ANNULLMENT.PLACEHOLDER.DATE_END'),
  );
  placeholder = input<string>('dd/MM/yyyy');

  startChange = output<Date | null>();
  endChange = output<Date | null>();
  rangeChange = output<{ start: Date | null; end: Date | null }>();

  startModel = model<Date | null>(null);
  endModel = model<Date | null>(null);
  maxDateModel = signal<Date | undefined>(undefined);

  /** El rango de partida se resuelve —y se anuncia— una sola vez. */
  private rangoDePartidaAnunciado = false;

  constructor() {
    super();
    effect(() => {
      const systemDateNow = this.systemDate();
      if (systemDateNow === null) return;
      // Solo la fecha del sistema dispara el rango de partida; leer el modelo aquí
      // haría que el efecto se repitiera (y lo repusiera) al primer cambio del usuario.
      untracked(() => {
        if (this.debeConservarRango()) {
          this.maxDateModel.set(this.fechaDelSistemaSinHora());
        } else {
          this.getDefaultStart();
          this.getDefaultEnd();
        }
        this.anunciarElRangoDePartida();
      });
    });
  }

  /**
   * Anuncia el rango de partida que el selector acaba de fijar.
   *
   * <p>Sin esto el selector ESCRIBE su rango pero no lo dice: la pantalla que solo escucha sus
   * eventos se queda sin rango y acaba presentando unas fechas que nunca llega a aplicar, de modo
   * que el listado devuelve registros de fuera del periodo que el usuario esta viendo.</p>
   *
   * <p>Se anuncia UNA sola vez. El rango de partida se fija cuando llega la fecha del sistema; a
   * partir de ahi los cambios son del usuario y ya viajan por los eventos de siempre.</p>
   */
  private anunciarElRangoDePartida(): void {
    if (this.rangoDePartidaAnunciado) return;

    const start = this.startModel();
    const end = this.endModel();
    if (!DateRangePickerComponent.esFecha(start) || !DateRangePickerComponent.esFecha(end)) return;

    this.rangoDePartidaAnunciado = true;
    this.startChange.emit(start);
    this.endChange.emit(end);
    this.rangeChange.emit({ start, end });
  }

  /**
   * Dice si el valor es una fecha con la que se puede consultar.
   *
   * <p>La fecha del sistema llega de una consulta que puede responder VACIA sin dar error; cuando
   * eso pasa lo que se arma es una fecha invalida, y anunciarla haria que la pantalla filtrara por
   * «NaN-NaN-NaN».</p>
   *
   * @param fecha fecha resuelta por el selector
   * @returns `true` si se puede anunciar
   */
  private static esFecha(fecha: Date | null): fecha is Date {
    return fecha instanceof Date && !Number.isNaN(fecha.getTime());
  }

  ngOnInit(): void {
    this.getSystemParams();
  }

  systemDate = signal<Date | null>(null);

  onStartChange(date: Date | null): void {
    this.startChange.emit(date);
    this.rangeChange.emit({ start: date, end: this.endModel() });
  }

  onEndChange(date: Date | null): void {
    this.endChange.emit(date);
    this.rangeChange.emit({ start: this.startModel(), end: date });
  }

  onRangeChange(range: { start: Date | null; end: Date | null }): void {
    this.rangeChange.emit(range);
  }

  /** Rango vigente, en la forma que lleva el campo de rango del kit. */
  protected readonly rango = computed<DateRange>(() => ({
    start: this.startModel(),
    end: this.endModel(),
  }));

  /**
   * El campo de rango del kit entrega el rango completo de una vez. Se reparte en los dos modelos
   * y se anuncia por los eventos de siempre, de modo que las pantallas no cambian.
   *
   * @param range rango elegido
   */
  protected alElegirRango(range: DateRange): void {
    this.startModel.set(range.start);
    this.endModel.set(range.end);
    this.startChange.emit(range.start);
    this.endChange.emit(range.end);
    this.rangeChange.emit(range);
  }

  private getDefaultStart() {
    const systemDateNow = this.systemDate();
    const now = systemDateNow ?? new Date();
    const value = new Date(
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01` + 'T00:00:00',
    );
    this.startModel.set(value);
  }

  private getDefaultEnd() {
    const value = this.fechaDelSistemaSinHora();
    this.endModel.set(value);
    this.maxDateModel.set(value);
  }

  /**
   * Fecha del sistema a las 00:00, que es el fin del rango de partida y el tope del selector.
   *
   * @returns la fecha del sistema sin hora
   */
  private fechaDelSistemaSinHora(): Date {
    const now = this.systemDate() ?? new Date();
    return new Date(
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}` +
        'T00:00:00',
    );
  }

  /**
   * Dice si hay que respetar el rango que trae el modelo en vez de reponer el de partida.
   *
   * @returns `true` si se pidió conservar el rango y el modelo trae dos fechas válidas
   */
  private debeConservarRango(): boolean {
    return (
      this.conservarRango() &&
      DateRangePickerComponent.esFecha(this.startModel()) &&
      DateRangePickerComponent.esFecha(this.endModel())
    );
  }

  getSystemParams() {
    this.service
      .getSystemParams(this.module())
      .pipe(
        tap((response) => {
          this.systemDate.set(new Date(response.fechaSistema));
        }),
        catchError((error) => {
          this.setError(error?.error.message || 'No se pudo cargar fecha del sistema');
          this.systemDate.set(null);
          return of(null);
        }),
      )
      .subscribe();
  }
}
